import { useDraggable } from "@dnd-kit/react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { CopyComponent } from "../../../../../parts/CopyComponent";
import {
  formatCustomUnitMeasurementEquivalenceSuffix,
  parseUnitToShow,
} from "../../../../../utils/nutrition-util";
import {
  getFoodDragData,
  getFoodDraggableId,
} from "../utils/meal-drag-drop-helpers";
import { MacrosDisplay } from "./MacrosDisplay";
import { RecipeTypeLabel } from "./RecipeTypeLabel";

/**
 * FoodItem
 *
 * Shared component to display a single food item with its details and macros
 * Used in both detailed and simplified meal planners
 * Supports drag and drop and copy functionality
 */
export const FoodItem = ({
  name,
  brand,
  quantityNumber,
  unitOfMeasure,
  quantityDescription,
  foodType,
  proteins = 0,
  carbs = 0,
  fats = 0,
  calories = 0,
  // Drag and drop props
  food,
  foodMealPlanId,
  foodIndex = 0,
  day,
  mealType,
  planMode,
  week,
  viewMode,
  query,
  hideHeaderActions = false,
}) => {
  const { t } = useTranslation("athlete-portal");
  const displayName =
    brand && !name.toLowerCase().includes(brand.toLowerCase())
      ? `${name} (${brand})`
      : name;
  const quantityBase = `${quantityNumber} ${parseUnitToShow(
    quantityNumber,
    foodType,
    unitOfMeasure,
    quantityDescription
  )}`;
  const quantityDisplay = `${quantityBase}${formatCustomUnitMeasurementEquivalenceSuffix(
    quantityNumber,
    unitOfMeasure,
    food?.foodId
  )}`;

  const displayFoodType =
    foodType === "external_recipe" ? "external-recipe" : foodType;

  const currentPlanMode = planMode || "detailed";
  const isDraggable =
    !hideHeaderActions &&
    !viewMode &&
    food &&
    day &&
    mealType &&
    foodMealPlanId != null;
  const showActions = !hideHeaderActions && food && !viewMode;

  // Draggable hook for food - always initialize, control with disabled
  const {
    ref: refDraggable,
    handleRef: handleRefDraggable,
    attributes,
    listeners,
    isDragging,
  } = useDraggable({
    id: isDraggable
      ? getFoodDraggableId(
          currentPlanMode,
          day,
          mealType,
          foodMealPlanId,
          foodIndex
        )
      : `food-static-${foodMealPlanId ?? "nodrag"}-${foodIndex}`,
    disabled: !isDraggable,
    data: isDraggable
      ? getFoodDragData(
          currentPlanMode,
          day,
          mealType,
          food,
          week,
          foodMealPlanId,
          foodIndex
        )
      : undefined,
  });

  return (
    <div
      className={`full-info ${isDragging ? "pointer-events-none opacity-0" : ""}`}
      ref={viewMode || hideHeaderActions ? null : refDraggable}
      role={isDraggable ? undefined : "presentation"}
      tabIndex={isDraggable ? undefined : -1}
    >
      <div className="grouped-meal-header">
        <div className="grouped-meal-header-content">
          <span className="title">{displayName}</span>
          <span className="subtitle">{quantityDisplay}</span>
          <RecipeTypeLabel
            foodType={displayFoodType}
            recipeId={food?.foodId?._id}
          />
        </div>
        {showActions && (
          <div
            className={`header-actions mobile-hidden ${isDragging ? "hidden!" : ""}`}
          >
            <CopyComponent
              type="food"
              food={food}
              day={day}
              query={query}
              planMode={currentPlanMode}
            />
            <div
              className="drag-icon cursor-grab"
              ref={handleRefDraggable}
              {...attributes}
              {...listeners}
              role="div"
            >
              <img
                src="/img/new-icons/drag-outline.svg"
                alt={t("foodItem.dragAlt")}
              />
            </div>
          </div>
        )}
      </div>
      <MacrosDisplay
        proteins={proteins}
        carbs={carbs}
        fats={fats}
        calories={calories}
      />
    </div>
  );
};

FoodItem.propTypes = {
  name: PropTypes.string.isRequired,
  brand: PropTypes.string,
  quantityNumber: PropTypes.number.isRequired,
  unitOfMeasure: PropTypes.string,
  quantityDescription: PropTypes.string,
  foodType: PropTypes.string,
  proteins: PropTypes.number,
  carbs: PropTypes.number,
  fats: PropTypes.number,
  calories: PropTypes.number,
  // Drag and drop props
  food: PropTypes.object,
  foodMealPlanId: PropTypes.string,
  day: PropTypes.object,
  mealType: PropTypes.string,
  planMode: PropTypes.string,
  week: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  viewMode: PropTypes.bool,
  query: PropTypes.object,
  hideHeaderActions: PropTypes.bool,
};
